import { useGetAddressMutation } from "apollo/hooks/mutations";
import {
  DELIVERY_ADDRESS_CLINIC_ADDRESS,
  DELIVERY_ADDRESS_NEW_ADDRESS,
  DELIVERY_ADDRESS_PATIENT_ADDRESS,
} from "core/consts";
import { buildLine } from "core/model/utils/strings";
import {
  Address,
  Auction,
  CareproviderAddress,
  EncryptedField,
  Location,
} from "core/types";
import Translations from "translations/types";

export function formatSearchLocationString({
  city,
  floor,
  houseNumber,
  street,
  translations,
  zipcode,
}: {
  city?: string;
  houseNumber?: string | null;
  street?: string;
  zipcode?: string;
  floor?: string;
  translations?: Translations;
}) {
  return buildLine(
    zipcode,
    city ? `${city},` : undefined,
    street,
    houseNumber,
    floor ? translations?.patient?.patientInformation?.floor : undefined,
    floor,
  );
}

export function formatSearchDestinationString({
  city,
  encrypted_house_number,
  street,
  zipcode,
}: {
  city?: string;
  encrypted_house_number?: string | null;
  street?: string;
  zipcode?: string;
}) {
  return `${zipcode} ${city}, ${street} ${encrypted_house_number}`;
}

// --- formatLocation ---
export function formatLocation({
  hideStreetAndHouseNumber,
  location,
  showFloor,
  startWithZipcode,
  translations,
}: {
  hideStreetAndHouseNumber?: boolean;
  location: (Address & Location) | undefined;
  showFloor?: boolean;
  startWithZipcode?: boolean;
  translations: Translations;
}) {
  if (!location) return "";

  const hasHouseNumber = !!(
    location?.encrypted_house_number || location?.street_number
  );
  const houseNumber =
    location?.encrypted_house_number?.decrypted || location?.street_number;
  let address = "";

  if (hideStreetAndHouseNumber) {
    // Only show zipcode and city
    if (location?.zipcode) {
      address += location.zipcode;
    }
    if (location?.city) {
      address += ` ${location.city}`;
    }
    return address === "" ? null : address;
  }

  if (hasHouseNumber) {
    if (location?.zipcode) {
      address += location.zipcode;
    }
    if (location?.city) {
      address += ` ${location.city}, `;
    }
    if (location?.street && houseNumber) {
      address += `${location.street} ${houseNumber}`;
    } else if (location?.street) {
      address += `${location.street}`;
    }
    if (location?.floor?.length && showFloor) {
      address += ` ${translations.patient.patientInformation.floor} ${location.floor}`;
    }
  } else if (startWithZipcode) {
    if (location?.zipcode) {
      address += location.zipcode;
      if (location?.city) {
        address += location?.street
          ? ` ${location.city}, ${location.street}`
          : ` ${location.city}`;
      }
    }
  } else {
    if (location?.zipcode && location?.street) {
      address += `${location.street}, `;
    }
    if (location?.zipcode) {
      address += location.zipcode;
    }
    if (location?.city) {
      address += ` ${location.city}`;
    }
  }
  return address === "" ? null : address;
}

// --- formatAddress ---
export const formatAddress = (providerAddress: CareproviderAddress): string => {
  const { address, city, zip_code } = providerAddress || {};
  const components = [
    address,
    zip_code && address ? `, ${zip_code}` : zip_code,
    (zip_code || address) && city ? ` ${city}` : city,
  ].filter(Boolean);
  return components.join("");
};

// --- formatMedicalSuppliesLocation ---
export function formatMedicalSuppliesLocation(
  auction: Auction | undefined | null,
): string | undefined {
  const medicalSupplies = auction?.medical_supplies;
  const deliveryType = medicalSupplies?.delivery_address_type;

  if (medicalSupplies && deliveryType) {
    switch (deliveryType) {
      case DELIVERY_ADDRESS_CLINIC_ADDRESS: {
        const careseekerAddress = auction.patient.careseeker?.address;

        if (careseekerAddress?.zipcode?.length) {
          return careseekerAddress.zipcode;
        }

        break;
      }

      case DELIVERY_ADDRESS_NEW_ADDRESS: {
        if (medicalSupplies?.delivery_address?.zipcode?.length) {
          return medicalSupplies?.delivery_address.zipcode;
        }
      }
    }
  }

  return auction?.profile?.search_location?.zipcode ?? undefined;
}

// Helper function to check if a string is likely a house number
export function isLikelyHouseNumber(str: string): boolean {
  // Match patterns like: "10", "10A", "10-12", "10/12", "10B"
  return /^\d+[A-Za-z]?(-\d+)?(\/\d+)?$/.test(str);
}

export function parseStreetAddress(streetAddress: string | undefined | null): {
  houseNumber: string | undefined;
  street: string | undefined;
} {
  if (!streetAddress) {
    console.error("Missing required fields in parseStreetAddress", {
      module: "kis_import",
      streetAddress: !!streetAddress,
    });
    return { houseNumber: undefined, street: undefined };
  }

  const parts = streetAddress.split(" ");
  const lastPart = parts[parts.length - 1];
  const lastPartIsHouseNumber = isLikelyHouseNumber(lastPart);

  return {
    houseNumber: lastPartIsHouseNumber ? lastPart : undefined,
    street: lastPartIsHouseNumber
      ? parts.slice(0, -1).join(" ")
      : streetAddress,
  };
}

// --- formatSearchLocation ---
export async function formatSearchLocation({
  city,
  getAddress,
  onError,
  postal_code,
  street_address,
}: {
  city?: string;
  getAddress?: ReturnType<typeof useGetAddressMutation>[0];
  onError?: (error: Error) => void;
  postal_code?: string;
  street_address?: string;
}): Promise<Location | undefined> {
  if (!street_address || !city || !postal_code) {
    console.error("Missing required fields in formatSearchLocation", {
      has_city: !!city,
      has_postal_code: !!postal_code,
      has_street_address: !!street_address,
      module: "kis_import",
    });
    return undefined;
  }

  const streetAddressParts = street_address.split(" ");
  const lastPart = streetAddressParts[streetAddressParts.length - 1];
  const isLastPartHouseNumber = isLikelyHouseNumber(lastPart);

  if (!isLastPartHouseNumber) {
    console.warn("No valid house number found in street address", {
      last_part: lastPart,
      module: "kis_import",
      street_address,
    });
  }

  const street = isLastPartHouseNumber
    ? streetAddressParts.slice(0, -1).join(" ")
    : street_address;
  const house_number = isLastPartHouseNumber ? lastPart : undefined;
  const zipcode = postal_code;
  let coordinates;
  let radius_in_meter;
  try {
    if (getAddress) {
      const data = await getAddress({
        city,
        country: "DE",
        house_number: house_number ?? "",
        street,
        zipcode,
      });
      coordinates = data?.data?.address?.coordinates;
      radius_in_meter = data?.data?.address?.radius;

      if (!coordinates) {
        console.warn("No coordinates returned from getAddress", {
          city,
          module: "kis_import",
          street_address,
          zipcode,
        });
      }
    }
  } catch (err) {
    console.error("Error in getAddress call", {
      city,
      error: err,
      module: "kis_import",
      street_address,
      zipcode,
    });
    onError?.(err as Error);
  }
  return {
    city,
    latitude: coordinates?.latitude,
    longitude: coordinates?.longitude,
    radius_in_meter,
    street,
    ...(house_number && {
      encrypted_house_number: { decrypted: house_number } as EncryptedField,
    }),
    zipcode,
  };
}

// --- locationValue ---
export function locationValue(
  auction: Auction | null | undefined,
  translations: Translations,
) {
  const formatLocations = (location: any) => {
    return location
      ? formatLocation({ location, showFloor: true, translations })
      : "";
  };
  switch (auction?.medical_supplies?.delivery_address_type) {
    case DELIVERY_ADDRESS_PATIENT_ADDRESS: {
      return {
        deliveryAddressPrefix: translations.address.patientAddress,
        deliveryAddressString: formatLocations(
          auction.profile?.search_location,
        ),
      };
    }
    case DELIVERY_ADDRESS_CLINIC_ADDRESS: {
      return {
        deliveryAddressPrefix: translations.address.clinicAddress,
        deliveryAddressString: formatLocations(
          auction.patient.careseeker?.address,
        ),
      };
    }
    case DELIVERY_ADDRESS_NEW_ADDRESS: {
      return {
        deliveryAddressPrefix: translations.address.anotherAddress,
        deliveryAddressString: formatLocations(
          auction.medical_supplies.delivery_address,
        ),
      };
    }
    default:
      return { deliveryAddressPrefix: "", deliveryAddressString: "" };
  }
}
